import { COMMERCIAL_ROLE, SUPER_ADMIN_ROLE } from 'helper/roles';
import useCrmContext from 'hooks/useCrmContext';
import Usefetchdata from 'hooks/usefetchdata';
import { useEffect, useState } from 'react';
import crmApi from 'server/crm/crmApi';

const EMPTY = { status: 'loading', position: '', leadStatus: '' };
// What the opening screen already knows (a contact page): shown at once, then
// read again quietly.
const seeds = new Map();
export const seedContactCrmFields = (memberId, { position, leadStatus }) =>
  seeds.set(String(memberId), { status: 'ready', position: position ?? '', leadStatus: leadStatus ?? '' });
const initialRead = (memberId) => seeds.get(String(memberId)) ?? EMPTY;

/**
 * What the CRM knows of a member, beside their account: their position (kept
 * by e-mail, the same at all their companies; admin/crm/company/crm lists it)
 * and their lead status. The position is written by the agency
 * manager and the sales reps only (`canEditPosition`). `save(email)` sends
 * what changed and resolves once done; `changed` says whether there is any.
 */
const useContactCrmFields = (companyId, memberId) => {
  const { call } = useCrmContext();
  const { userData } = Usefetchdata();
  const [read, setRead] = useState(() => initialRead(memberId));
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (!companyId || !memberId) return undefined;
    let current = true;
    setRead(initialRead(memberId));
    setDraft({});
    // Two light reads, never the contact page's record: its requests carry
    // abort keys, and a second read of it would cancel the page's own.
    Promise.all([
      call(crmApi.getCompanyCrm, { companyId }),
      call(crmApi.getLeadStatus, { personId: memberId }),
    ]).then(
      ([company, lead]) =>
        current &&
        setRead({ status: 'ready', position: company?.memberPositions?.[memberId] ?? '', leadStatus: lead?.leadStatus ?? '' }),
      // A known value stays shown when the quiet read fails.
      () => current && setRead((previous) => (previous.status === 'ready' ? previous : { ...EMPTY, status: 'error' })),
    );
    return () => {
      current = false;
    };
  }, [call, companyId, memberId]);

  const position = draft.position ?? read.position;
  const leadStatus = draft.leadStatus ?? read.leadStatus;
  const canEditPosition = read.status === 'ready' && [SUPER_ADMIN_ROLE, COMMERCIAL_ROLE].includes(Number(userData?.role));
  const positionChanged = canEditPosition && position.trim() !== read.position;
  const leadChanged = read.status === 'ready' && leadStatus !== read.leadStatus;

  const save = async (email) => {
    if (positionChanged && email) await call(crmApi.setContactPosition, { email, position: position.trim() });
    if (leadChanged) await call(crmApi.setLeadStatus, { personId: memberId, status: leadStatus });
    setRead((previous) => ({ ...previous, position: position.trim(), leadStatus }));
    setDraft({});
  };

  return {
    status: read.status,
    position,
    leadStatus,
    canEditPosition,
    changed: positionChanged || leadChanged,
    setPosition: (value) => setDraft((previous) => ({ ...previous, position: value })),
    setLeadStatus: (value) => setDraft((previous) => ({ ...previous, leadStatus: value })),
    save,
  };
};

export default useContactCrmFields;
